import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import ScholarAvatar from "@/components/ScholarAvatar";
import VerifiedBadge from "@/components/VerifiedBadge";
import BrandMark from "@/components/BrandMark";
import Seo from "@/components/Seo";
import { Heart, MessageCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PostPreview() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/posts/${id}`)
      .then((r) => setPost(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "notfound" : "error"));
  }, [id]);

  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)]">
      {post && (
        <Seo
          title={`${post.author?.full_name || "A scholar"} on VaidGram`}
          description={(post.content || "").slice(0, 155)}
          path={`/post/${id}`}
          type="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "SocialMediaPosting",
            "author": { "@type": "Person", "name": post.author?.full_name || "VaidGram scholar" },
            "articleBody": post.content,
            "datePublished": post.created_at,
            "url": `https://vaidgram.in/post/${id}`,
            "publisher": { "@type": "Organization", "name": "VaidGram" }
          }}
        />
      )}
      <header className="border-b border-[color:var(--ayur-border)] bg-white/70 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="preview-brand" className="flex items-center gap-2 text-[color:var(--ayur-emerald)]">
            <BrandMark size={40} />
            <span className="font-serif-display text-2xl font-semibold">VaidGram</span>
          </Link>
          <Link to="/signup">
            <Button data-testid="preview-join-cta-header" className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white rounded-full">
              Join VaidGram
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {error === "notfound" && (
          <div className="paper-card p-12 text-center">
            <h1 className="font-serif-display text-3xl text-[color:var(--ayur-emerald)]">Post not found</h1>
            <p className="text-[color:var(--ayur-mute)] mt-2">It may have been removed by the author.</p>
            <Link to="/" className="inline-block mt-6 text-[color:var(--ayur-terracotta)] link-underline">Back to VaidGram</Link>
          </div>
        )}
        {error === "error" && (
          <div className="paper-card p-12 text-center text-[color:var(--ayur-mute)]">Could not load this post.</div>
        )}
        {!error && !post && (
          <div className="paper-card p-12 text-center text-[color:var(--ayur-mute)]">Loading…</div>
        )}
        {post && (
          <>
            <article data-testid="public-post-card" className="paper-card p-6 sm:p-8 animate-fade-up">
              <div className="flex items-start gap-3">
                <ScholarAvatar user={post.author} size={48} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[color:var(--ayur-ink)] inline-flex items-center gap-1.5">
                    {post.author?.full_name || "A VaidGram scholar"}
                    {post.author?.verified && <VerifiedBadge size={16} />}
                  </div>
                  <div className="text-xs text-[color:var(--ayur-mute)]">
                    {post.author?.degree}
                    {post.author?.specialty ? ` · ${post.author.specialty}` : ""}
                    {post.author?.college ? ` · ${post.author.college}` : ""}
                  </div>
                </div>
                <span className="text-xs text-[color:var(--ayur-mute)] whitespace-nowrap">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                </span>
              </div>
              <div className="mt-5 whitespace-pre-wrap text-[color:var(--ayur-ink)] leading-relaxed text-lg">
                {post.content}
              </div>
              {post.specialty && (
                <div className="mt-4"><span className="pill">{post.specialty}</span></div>
              )}
              <div className="mt-5 flex items-center gap-4 text-sm text-[color:var(--ayur-mute)] border-t border-[color:var(--ayur-border)] pt-3">
                <span className="inline-flex items-center gap-1"><Heart className="w-4 h-4" />{post.like_count}</span>
                <span className="inline-flex items-center gap-1"><MessageCircle className="w-4 h-4" />{post.comment_count}</span>
              </div>
            </article>

            <div data-testid="preview-join-cta" className="mt-8 p-6 sm:p-8 rounded-2xl text-center bg-[color:var(--ayur-emerald)] text-white shadow-lg">
              <div className="label-eyebrow text-[color:var(--ayur-gold)]">Only for Ayurveda scholars</div>
              <h2 className="font-serif-display text-3xl mt-2 text-white">Join the conversation</h2>
              <p className="text-white/85 mt-2">Sign in with your BAMS college & batch to reply, follow, and connect with peers.</p>
              <Link to="/signup" className="inline-block mt-5">
                <Button data-testid="preview-join-cta-btn" className="bg-[color:var(--ayur-gold)] hover:bg-[color:var(--ayur-gold)]/90 text-white rounded-full px-8">
                  Join VaidGram <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <div className="text-xs text-white/70 mt-3">
                Already a scholar? <Link to="/login" className="underline">Log in</Link>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
